/**
 * Одна строка о ходе работы — и она не должна прыгать.
 *
 * Поле `files.done` значит у приёмника разное в разных кадрах: в кадре записи
 * это записанные файлы, в кадре сверки — осмотренные записи описи. Кадры идут
 * вперемешку, по два-три раза в минуту, и строка, собранная из последнего
 * пришедшего, скачет между «2 553 файлов» и «4 194 304 записей осмотрено».
 *
 * Владелец назвал это точно: «опять гонка за экран». Первый раз она была за
 * весь экран — прибор сменялся подготовкой и обратно; теперь за подпись под
 * числом.
 *
 * Правило здесь одно: **число и подпись обязаны описывать одну работу.** Пока
 * идёт чтение, показываем записанные БАЙТЫ — они растут монотонно и не зависят
 * от того, чем приёмник занят сию секунду, — и называем происходящее его
 * словом. Файлы называем файлами только в кадрах записи.
 */

/** Фазы, в которых приёмник читает, а не пишет. Их слова, наш список. */
export const READING = new Set(['survey', 'manifest', 'compare', 'chunks', 'verify']);

/** Чем занят, коротко — для строки списка, где места на фразу нет. */
const SHORT: Record<string, string> = {
  survey: 'читает перечни',
  manifest: 'читает перечень',
  compare: 'сверяет',
  chunks: 'берёт карту',
  verify: 'проверяет',
};

export function counterWord(phase: string | null, moved = true): string {
  // В `survey` итог РАСТЁТ по ходу: это сумма обещанного снимком и уже
  // осмотренного на диске, а сколько на приёмнике записей — заранее
  // неизвестно (234). Поэтому «из N» здесь не обещание, а текущее знание.
  if (phase === 'survey') return 'записей осмотрено';
  if (phase === 'manifest') return 'записей перечня';
  if (phase === 'compare') return 'файлов сверено';
  if (phase === 'chunks') return 'локаций чанков';
  if (phase === 'verify') return 'записей проверено';
  // Пока не пошли байты, «файлов» — догадка: приёмник считает этим полем то,
  // чем занят, и в сверке описи это записи описи, а не файлы снимка. Их
  // бывает больше, чем файлов в снимке, и «6 238 045 из 4 039 822» читалось бы
  // как поломка.
  return moved ? 'файлов' : 'записей';
}

export interface Told {
  /** Число с подписью — то, что видно в строке. */
  text: string;
  /** Правда ли это про записанные файлы. Ложь — про чтение. */
  aboutWriting: boolean;
}

/**
 * Что сказать о работе одной строкой.
 *
 * `count` и `bytes` приходят снаружи: форматирование живёт в своём модуле, а
 * это правило — про то, ЧТО показывать, а не как.
 */
export function tell(
  p: {
    phase?: string | null;
    filesDone: number;
    filesTotal?: number | null;
    bytesWritten?: number | null;
    bytesRead: number;
    bytesUploaded: number;
  },
  fmt: { count: (n: number) => string; bytes: (n: number) => string },
): Told {
  const written = p.bytesWritten ?? 0;
  const moved = written > 0 || p.bytesRead > 0 || p.bytesUploaded > 0;
  const reading = p.phase != null && READING.has(p.phase);

  if (moved && reading) {
    /*
     * Работа идёт, но сейчас читают. Файлы называть нельзя — счётчик в этом
     * кадре про опись, — а записанные байты и растут монотонно, и отвечают на
     * вопрос, ради которого в строку смотрят.
     */
    return {
      text: `${fmt.bytes(written || p.bytesUploaded)} записано · ${SHORT[p.phase!] ?? p.phase!}`,
      aboutWriting: false,
    };
  }
  if (moved) {
    return {
      text: `${fmt.count(p.filesDone)} файлов · ${fmt.bytes(written || p.bytesUploaded)}`,
      aboutWriting: true,
    };
  }
  // Байты ещё не шли: показываем то, что приёмник считает, его же словом.
  const total = p.filesTotal != null && p.filesTotal > p.filesDone
    ? ` из ${fmt.count(p.filesTotal)}` : '';
  return {
    text: `${fmt.count(p.filesDone)}${total} ${counterWord(p.phase ?? null, false)}`,
    aboutWriting: false,
  };
}
